import React, { useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { Product } from '../types';
import { 
  hasMultipleCaseOptions, 
  getCaseQuantityDisplayText,
  formatCasePrice,
  getCasePricingInfo,
  allowsCustomCases,
  getCustomCaseDisplayText,
} from '../utils/caseQuantity';
import CustomCaseInput from './CustomCaseInput';

interface CaseQuantitySelectorProps {
  product: Product;
  selectedCaseQuantity: number;
  onCaseQuantityChange: (caseQuantity: number, isCustom?: boolean) => void;
  size?: 'small' | 'medium';
  showPricing?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  isCustomCase?: boolean;
  onCustomCaseToggle?: (isCustom: boolean) => void;
}

const CaseQuantitySelector: React.FC<CaseQuantitySelectorProps> = ({
  product,
  selectedCaseQuantity,
  onCaseQuantityChange,
  size = 'medium',
  showPricing = true,
  variant = 'outlined',
  isCustomCase = false,
  onCustomCaseToggle,
}) => {
  const CUSTOM_CASE_VALUE = -1; // Special value to indicate custom case selection
  const [localIsCustom, setLocalIsCustom] = useState(isCustomCase);
  
  const hasMultiple = hasMultipleCaseOptions(product);
  const caseQuantities = product.case_quantities || (product as any).case_quantity || [1];
  const maxCaseQty = Math.max(...caseQuantities);
  
  // Only show as chip if max case quantity is 1 (no custom cases possible)
  // Otherwise, always show dropdown with custom case option
  const allowsCustom = maxCaseQty > 1;

  // If max quantity is 1, no custom cases possible - show as a chip/label
  if (maxCaseQty === 1) {
    const singleQuantity = caseQuantities[0];
    const pricingInfo = getCasePricingInfo(product, singleQuantity);
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          label={getCaseQuantityDisplayText(singleQuantity)}
          variant="outlined"
          color="primary"
          size={size}
        />
        {showPricing && pricingInfo && (
          <Typography variant="body2" color="text.secondary">
            {formatCasePrice(pricingInfo.total_price_cents)}
          </Typography>
        )}
      </Box>
    );
  }

  const handleSelectionChange = (value: number) => {
    if (value === CUSTOM_CASE_VALUE) {
      // Switch to custom case mode
      setLocalIsCustom(true);
      if (onCustomCaseToggle) {
        onCustomCaseToggle(true);
      }
      // Set a default custom quantity (e.g., 1 or half of max)
      const defaultCustomQty = Math.max(1, Math.floor((caseQuantities[caseQuantities.length - 1] || 10) / 2));
      onCaseQuantityChange(defaultCustomQty, true);
    } else {
      // Standard case selected
      setLocalIsCustom(false);
      if (onCustomCaseToggle) {
        onCustomCaseToggle(false);
      }
      onCaseQuantityChange(value, false);
    }
  };

  const handleCustomQuantityChange = (customQty: number) => {
    onCaseQuantityChange(customQty, true);
  };

  // If custom case is active, show the custom input
  if (localIsCustom) {
    return (
      <Box>
        <FormControl variant={variant} size={size} fullWidth sx={{ mb: 2 }}>
          <Select
            value={CUSTOM_CASE_VALUE}
            onChange={(e) => handleSelectionChange(Number(e.target.value))}
          >
            {caseQuantities.map((quantity) => {
              const pricingInfo = getCasePricingInfo(product, quantity);
              const displayText = getCaseQuantityDisplayText(quantity);
              
              return (
                <MenuItem key={quantity} value={quantity}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Typography>{displayText}</Typography>
                    {showPricing && pricingInfo && (
                      <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
                        {formatCasePrice(pricingInfo.total_price_cents)}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
            {allowsCustom && (
              <MenuItem value={CUSTOM_CASE_VALUE}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>
                    Custom Case
                  </Typography>
                  <Chip label="+$10" size="small" color="warning" />
                </Box>
              </MenuItem>
            )}
          </Select>
        </FormControl>
        
        <CustomCaseInput
          product={product}
          value={selectedCaseQuantity}
          onChange={handleCustomQuantityChange}
          size={size}
        />
      </Box>
    );
  }

  // Standard case selection dropdown
  return (
    <FormControl variant={variant} size={size} fullWidth>
      <Select
        value={selectedCaseQuantity}
        onChange={(e) => handleSelectionChange(Number(e.target.value))}
      >
        {caseQuantities.map((quantity) => {
          const pricingInfo = getCasePricingInfo(product, quantity);
          const displayText = getCaseQuantityDisplayText(quantity);
          
          return (
            <MenuItem key={quantity} value={quantity}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography>{displayText}</Typography>
                {showPricing && pricingInfo && (
                  <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
                    {formatCasePrice(pricingInfo.total_price_cents)}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
        {allowsCustom && (
          <MenuItem value={CUSTOM_CASE_VALUE}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography sx={{ fontWeight: 600, color: 'warning.main' }}>
                Custom Case
              </Typography>
              <Chip label="+$10" size="small" color="warning" />
            </Box>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default CaseQuantitySelector; 